import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    Grid,
    Typography,
    IconButton,
    Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import { honorariosSolicitudDetalleJWT } from "../../api/endpoints";

const getEstadoColor = (idEstado) => {
    switch (idEstado) {
        case 3: return "primary"; // Borrador
        case 4: return "default"; // Cerrada
        case 0: return "secondary"; // Pendiente
        case 2: return "secondary"; // En Curso
        case 1: return "primary"; // Pagada
        case -1: return "default"; // Anulada
        default: return "default";
    }
};

export default function SolicitudDetalleDialog({ open, onClose, solicitudId }) {
    const [loading, setLoading] = useState(true);
    const [solicitud, setSolicitud] = useState(null);
    const [certificados, setCertificados] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && solicitudId) {
            fetchDetalle();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, solicitudId]);

    const fetchDetalle = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await honorariosSolicitudDetalleJWT(solicitudId);
            if (response.ok) {
                setSolicitud(response.solicitud);
                setCertificados(response.certificados || []);
            } else {
                setError(response.mensaje || "Error al cargar el detalle");
            }
        } catch (err) {
            if (err?.code === "SESSION_EXPIRED") {
                throw err;
            }
            setError(err?.message || "Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    const handleImprimir = () => {
        if (!solicitud || !certificados) return;
        // Construir HTML completo en un iframe con @media print y disparar print una sola vez
        const iframeId = "print-iframe-solicitud";
        const old = document.getElementById(iframeId);
        if (old) old.parentNode.removeChild(old);

        const rows = certificados.map(c =>
            `<tr><td>${c.FPago || '-'}</td><td>${c.Letra || ''}</td><td>${c.Numero || ''}</td>
             <td>${c.Expediente_Autos || '-'}</td><td>${c.Expediente_Radicacion || '-'}</td>
             <td>${c.Demandado_NN || '-'}</td>
             <td style="text-align:right">$ ${(parseFloat(c.Costas) || 0).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
             <td style="text-align:right">$ ${(parseFloat(c.Costas_IVA) || 0).toLocaleString('es-AR', {minimumFractionDigits:2})}</td>
             <td style="text-align:right">$ ${((parseFloat(c.Costas)||0)+(parseFloat(c.Costas_IVA)||0)).toLocaleString('es-AR', {minimumFractionDigits:2})}</td></tr>`
        ).join('');
        const total = (parseFloat(solicitud.Honorarios) || 0).toLocaleString('es-AR', {minimumFractionDigits:2});
        const html = `<!DOCTYPE html><html><head><style>
            @page { size: landscape; margin: 15mm; }
            body{font-family:Arial,sans-serif;margin:20px}
            h2{color:#2e7d32;margin-bottom:5px} table{width:100%;border-collapse:collapse;margin:15px 0}
            th,td{border:1px solid #ddd;padding:4px 6px;font-size:10px}
            th{background:#2e7d32;color:white} .info{margin:3px 0;font-size:12px}
            .total{text-align:right;font-size:14px;font-weight:bold;margin:15px 0}
        </style></head><body>
            <h2>Solicitud de Pago de Honorarios Nº ${solicitud.Id}</h2>
            <p class="info"><strong>Profesional:</strong> ${solicitud.Profesional} - Mat: ${solicitud.Matricula_Provincial}</p>
            <p class="info"><strong>Fecha:</strong> ${solicitud.Fecha} | <strong>Estado:</strong> ${solicitud.Estado}</p>
            ${solicitud.Expediente_Adm ? `<p class="info"><strong>Exp. Administrativo:</strong> ${solicitud.Expediente_Adm}</p>` : ''}
            <table><thead><tr><th>Fecha Plan</th><th>Serie</th><th>Certif</th><th>Autos</th><th>Juzgado</th><th>Demandado</th><th>Costas</th><th>IVA</th><th>Total</th></tr></thead>
            <tbody>${rows}</tbody></table>
            <p class="total">Total a Pagar: $ ${total}</p>
        </body></html>`;

        const iframe = document.createElement("iframe");
        iframe.id = iframeId;
        iframe.style.cssText = "position:fixed;top:-10000px;left:0;width:800px;height:600px;border:none";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
        // Print una sola vez después de renderizar
        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 3000);
        }, 300);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "4px",
                    background: "linear-gradient(180deg, #E8F5E9 0%, #ffffff 20%)",
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pr: 1,
                pt: 2,
                pb: 1,
            }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 500 }}>
                    {solicitud ? `Solicitud de Pago Nº ${solicitud.Id}` : "Detalle de Solicitud"}
                </span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error" variant="body1">
                        {error}
                    </Typography>
                ) : solicitud ? (
                    <>
                        <Box sx={{
                            background: "#ffffff",
                            borderRadius: "4px",
                            p: 2.5,
                            boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                        }} mb={3}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                        Fecha
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "text.primary" }}>
                                        {solicitud.Fecha}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                        Estado
                                    </Typography>
                                    <Chip
                                        label={solicitud.Estado}
                                        color={getEstadoColor(solicitud.Id_Estado)}
                                        sx={{ fontWeight: "bold" }}
                                        size="small"
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                        Total Honorarios
                                    </Typography>
                                    <Typography variant="h6" color="primary">
                                        $ {(parseFloat(solicitud.Honorarios) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                        Certificados
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "text.primary" }}>
                                        {certificados.length}
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                        Profesional
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: "text.primary" }}>
                                        {solicitud.Profesional} - Mat: {solicitud.Matricula_Provincial}
                                    </Typography>
                                </Grid>

                                {solicitud.Expediente_Adm && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                            Expediente Administrativo
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: "text.primary" }}>
                                            {solicitud.Expediente_Adm}
                                        </Typography>
                                    </Grid>
                                )}

                                {solicitud.Fecha_Pago && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                                            Fecha de Pago
                                        </Typography>
                                        <Typography variant="body1" color="primary">
                                            {solicitud.Fecha_Pago}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Typography variant="h6" gutterBottom>
                            Certificados Incluidos ({certificados.length})
                        </Typography>

                        <TableContainer component={Paper} sx={{
                            borderRadius: "4px",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
                            overflow: "hidden",
                        }}>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: "#E8F5E9" }}>
                                    <TableRow>
                                        <TableCell>Fecha Plan</TableCell>
                                        <TableCell>Fecha Inicio</TableCell>
                                        <TableCell>Autos</TableCell>
                                        <TableCell>Juzgado</TableCell>
                                        <TableCell>Nro Plan</TableCell>
                                        <TableCell>Serie</TableCell>
                                        <TableCell>Certif</TableCell>
                                        <TableCell align="right">Deuda</TableCell>
                                        <TableCell align="right">Costas</TableCell>
                                        <TableCell align="right">IVA</TableCell>
                                        <TableCell>Demandado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {certificados.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No hay certificados en esta solicitud
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        certificados.map((cert, idx) => (
                                            <TableRow key={cert.Id} hover sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell>{cert.FPago}</TableCell>
                                                <TableCell>{cert.Fecha_Inicio}</TableCell>
                                                <TableCell>{cert.Expediente_Autos}</TableCell>
                                                <TableCell>{cert.Expediente_Radicacion}</TableCell>
                                                <TableCell>{cert.Ref_Nro_Plan}</TableCell>
                                                <TableCell>{cert.Letra}</TableCell>
                                                <TableCell>{cert.Numero}</TableCell>
                                                <TableCell align="right">
                                                    $ {(parseFloat(cert.Deuda_Certificado) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    $ {(parseFloat(cert.Costas) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    $ {(parseFloat(cert.Costas_IVA) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                                <TableCell>{cert.Demandado_NN}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ p: 2, backgroundColor: "#f1f8e9", borderTop: "1px solid #c8e6c9" }}>
                <Button onClick={onClose}>
                    Cerrar
                </Button>
                <Button
                    onClick={handleImprimir}
                    color="primary"
                    variant="contained"
                    startIcon={<PrintIcon />}
                    disabled={!solicitud}
                >
                    Imprimir
                </Button>
            </DialogActions>
        </Dialog>
    );
}
